import { authExchange } from '@urql/exchange-auth';
import { requestPolicyExchange } from '@urql/exchange-request-policy';
import { retryExchange } from '@urql/exchange-retry';
import { Kind } from 'graphql';
import { get } from 'lodash';
import { type PropsWithChildren, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  type CombinedError,
  cacheExchange,
  createClient,
  fetchExchange,
  mapExchange,
  type Operation,
  Provider,
} from 'urql';

import { fullLogout, useAuthStore } from '@revelio/auth';
import { isLocalhost } from '@revelio/core';
import { isNonRetriableError } from '@revelio/data-access';
import type { KibanaLogger } from '@revelio/iso-utility';

import { environment } from '../../environments/environment';
import { recordTruncatedGraphQL } from '../../hooks/open-replay';
import { showAlertDialog } from '../components/alert-dialog';
import { useKibanaLogger } from '../hooks/kibana-logger/kibana-logger';
import { UrqlAndTanstackErrorMessage } from '../ui/urql-and-tanstack-error-message/urql-and-tanstack-error-message';
import { priorityExchange } from './priority-exchange';

// Register custom context types for urql operations
declare module 'urql' {
  interface OperationContext {
    skipGlobalErrorHandling?: boolean;
    priority?: 'high' | 'low';
    _startTime?: number;
  }
}

export const activeRequests = new Set<string>();

/**
 * Extract operation name from GraphQL operation
 */
const getOperationName = (operation: Operation): string => {
  return (
    operation?.query?.definitions?.find(
      (def) => def?.kind === Kind.OPERATION_DEFINITION
    )?.name?.value || String(operation.key)
  );
};

const logGqlError = (
  error: CombinedError,
  operation: Operation,
  kibanaLogger: KibanaLogger
) => {
  const { message, logOptions, isWarningLevel } = kibanaLogger.getUrqlErrorLog(
    error,
    operation
  );

  if (isWarningLevel) {
    console.warn(message);
    kibanaLogger.warn(message, logOptions);
  } else {
    console.error(message);
    kibanaLogger.error(message, logOptions);
  }
};

const skipErrorDialogMessages = [
  /** TODO: This is a temporary solution for talent discovery, when
   * we paginate beyond the total pages, supress the error. Once
   * we return total pages in the backend response, we can add a
   * permanent solution */
  'no user ids',
  'seat limit has been reached',
  '[Network] The user aborted a request.',
];

const displayErrorDialog = (
  error: CombinedError,
  operation: Operation,
  hideErrorDialog: boolean
) => {
  if (
    hideErrorDialog ||
    skipErrorDialogMessages.some((msg) => error.message.includes(msg))
  ) {
    return;
  }

  // Temporary error skipping for data builder column configuration getPipelineConfigs query during backend permission error (for non revelio admins)
  // since it shouldn't be blocking any users from finishing their deliverable configuration
  if (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // biome-ignore lint/suspicious/noExplicitAny: migrated from develop
    (operation.query.definitions[0] as any).name.value === 'getPipelineConfigs'
  ) {
    return;
  }

  const reqIdSearchResult = /(?:req-id:\s)(.*?)(?=,)/.exec(error.message);
  const reqId = get(reqIdSearchResult, '[1]');

  showAlertDialog({
    alertType: 'actionless',
    isOpen: true,
    headerText: 'Sorry!',
    bodyContent: <UrqlAndTanstackErrorMessage error={error} reqId={reqId} />,
  });
};

/*
 * Track requests in progress
 */
const requestTrackerExchange = mapExchange({
  onOperation(operation) {
    const operationName = getOperationName(operation);
    if (['query', 'mutation', 'subscription'].includes(operation.kind)) {
      activeRequests.add(operationName);
    }

    if (operation.kind === 'teardown' && activeRequests.has(operationName)) {
      activeRequests.delete(operationName);
    }

    return operation;
  },
  onResult(result) {
    if (['query', 'mutation'].includes(result.operation.kind)) {
      const operationName = getOperationName(result.operation);
      if (activeRequests.has(operationName)) {
        activeRequests.delete(operationName);
      }
    }

    return result;
  },
});

/**
 * Stamps _startTime immediately before the fetchExchange to reflect the network round-trip.
 */
const timingExchange = mapExchange({
  onOperation(operation) {
    return {
      ...operation,
      context: {
        ...operation.context,
        _startTime: performance.now(),
      },
    };
  },
});

/**
 * Observes each network-level operation once (after cache deduplication) for Kibana, error dialogs, and OpenReplay.
 * Placed after cacheExchange so logging is not multiplied by useQuery subscriber count.
 */
const makeNetworkObserverExchange = (
  kibanaLogger: KibanaLogger | undefined,
  hideErrorDialogs: boolean
) =>
  mapExchange({
    onError(error, operation) {
      if (
        !operation.context.skipGlobalErrorHandling &&
        kibanaLogger !== undefined
      ) {
        logGqlError(error, operation, kibanaLogger);
        displayErrorDialog(error, operation, hideErrorDialogs);
      }

      if (!operation.context.skipGlobalErrorHandling) {
        const startTime = operation.context._startTime;
        if (startTime !== undefined) {
          const duration = performance.now() - startTime;

          recordTruncatedGraphQL(
            operation.kind,
            getOperationName(operation),
            operation.variables,
            { error: error.message },
            duration
          );
        }
      }
    },
    onResult(result) {
      if (result.stale) {
        return result;
      }

      if (!['query', 'mutation'].includes(result.operation.kind)) {
        return result;
      }

      const startTime = result.operation.context._startTime;
      if (startTime === undefined) {
        return result;
      }

      const duration = performance.now() - startTime;

      recordTruncatedGraphQL(
        result.operation.kind,
        getOperationName(result.operation),
        result.operation.variables,
        result.data,
        duration
      );

      return result;
    },
  });

/** ================================
 * UrqlProvider
 ================================ */
export const UrqlProvider = ({
  children,
  hideErrorDialogs = false,
}: PropsWithChildren<{
  hideErrorDialogs?: boolean;
}>) => {
  const { kibanaLogger } = useKibanaLogger();

  const urqlClient = useMemo(() => {
    return createClient({
      url: `${environment.GO_API_ROOT}/query`,
      fetchOptions: {
        credentials: 'include',
      },
      exchanges: [
        requestPolicyExchange({}),
        cacheExchange,
        makeNetworkObserverExchange(kibanaLogger, hideErrorDialogs),
        requestTrackerExchange,
        authExchange(async ({ appendHeaders }) => {
          return {
            addAuthToOperation: (operation) => {
              const auth = useAuthStore.getState();
              const xBenchmarkId: 'e2e-tests' | undefined = (() => {
                if (
                  !isLocalhost() &&
                  auth?.ory?.username === 'testing.framework@reveliolabs.com'
                ) {
                  return 'e2e-tests';
                }

                return undefined;
              })();

              return appendHeaders(operation, {
                'x-request-id': crypto.randomUUID(),
                ...(xBenchmarkId && { 'X-Benchmark-Id': xBenchmarkId }),
              });
            },
            didAuthError: (error) => {
              const status = error.response?.status;
              if (status === 401 || status === 403) return true;
              else return false;
            },
            refreshAuth: async () => {
              fullLogout();
            },
          };
        }),
        retryExchange({
          initialDelayMs: 1000,
          maxDelayMs: 5000,
          maxNumberAttempts: 2,
          retryIf(error, operation) {
            if (isNonRetriableError(error)) return false;
            else {
              if (kibanaLogger && !operation.context.skipGlobalErrorHandling) {
                const { message, logOptions, isWarningLevel } =
                  kibanaLogger.getUrqlErrorLog(error, operation);

                const retryAttempt = (operation.context?.retry?.count ?? 0) + 1;

                const messageWithRetry = `Retrying failed request (Attempt ${retryAttempt})... ${message}`;
                /** Note: Only kibana error logs will alert the error slack channel */
                if (isWarningLevel) {
                  console.warn(messageWithRetry);
                  kibanaLogger.warn(messageWithRetry, logOptions);
                } else {
                  console.error(messageWithRetry);
                  kibanaLogger.error(messageWithRetry, logOptions);
                }
              }

              return true;
            }
          },
        }),
        priorityExchange,
        timingExchange,
        fetchExchange,
      ],
    });
  }, [kibanaLogger, hideErrorDialogs]);

  return <Provider value={urqlClient}>{children}</Provider>;
};

/** Route-aware UrqlProvider that handles error dialog visibility based on current route */
export const RouteAwareUrqlProvider = ({ children }: PropsWithChildren) => {
  const location = useLocation();
  const hideErrorDialogs = location.pathname === '/health-check';

  return (
    <UrqlProvider hideErrorDialogs={hideErrorDialogs}>{children}</UrqlProvider>
  );
};
